import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, Compass, Rocket, Award } from "lucide-react";
import { RevealLines, FadeUp } from "@/components/site/Reveal";
import { SERVICES } from "@/data/site";

const MANIFESTO = [
  {
    n: "01",
    icon: Compass,
    title: "La stratégie d'abord",
    body: "Chaque projet démarre par un diagnostic. Nous analysons votre image et vos objectifs avant de créer, pour des actions qui portent leurs fruits.",
  },
  {
    n: "02",
    icon: Rocket,
    title: "De l'idée à la diffusion",
    body: "Un seul studio pour penser, créer et diffuser : stratégie, identité, web, image et réseaux sociaux réunis sous un même toit.",
  },
  {
    n: "03",
    icon: Award,
    title: "Une exigence de groupe",
    body: "Filiale du Groupe Equinoxe, nous portons la même rigueur : réponse sous 24h, engagement et proximité.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const bannerRef = useRef(null);
  const { scrollYProgress: bannerP } = useScroll({ target: bannerRef, offset: ["start end", "end start"] });
  const bannerY = useTransform(bannerP, [0, 1], ["-10%", "10%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden">
        <motion.div style={{ opacity }} className="absolute inset-0 pointer-events-none">
          <motion.div
            style={{ y, scale }}
            className="absolute -right-32 top-24 w-[60vw] max-w-[720px] aspect-square rounded-full border-[3px] border-violet/25 flex items-center justify-center"
          >
            <img src="/enso.png" alt="" className="w-[60%] h-auto select-none" draggable="false" />
          </motion.div>
          <motion.div
            style={{ y }}
            className="absolute right-10 top-40 w-[40vw] max-w-[520px] aspect-square rounded-full border border-black/5"
          />
        </motion.div>

        <div className="relative mx-auto w-full max-w-[1400px] px-6 lg:px-12 pb-16 lg:pb-24 pt-40">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8 flex items-center gap-3 text-sm tracking-[0.25em] uppercase text-muted-foreground"
          >
            <span className="h-px w-10 bg-violet" /> Studio créatif · depuis 2004
          </motion.p>

          <h1 className="font-display font-extrabold tracking-tighter leading-[0.88] text-[15vw] sm:text-[13vw] lg:text-[10.5vw]">
            <RevealLines lines={["Analyser.", "Développer."]} delay={0.35} className="block" />
            <span className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.35 + 2 * 0.12 }}
              >
                Promouvoir<span className="text-violet">.</span>
              </motion.span>
            </span>
          </h1>

          <div className="mt-10 grid lg:grid-cols-12 gap-8 items-end">
            <FadeUp delay={0.9} className="lg:col-span-6">
              <p className="text-lg lg:text-xl text-muted-foreground max-w-xl leading-relaxed">
                Equinoxe Studio réunit stratégie, création graphique, développement web et production de contenus.
                Une seule équipe, de l'idée à la diffusion.
              </p>
            </FadeUp>
            <FadeUp delay={1.05} className="lg:col-span-6 lg:justify-self-end flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="group inline-flex items-center gap-3 bg-ink text-white px-8 py-4 hover:bg-violet transition-colors duration-300"
              >
                Démarrer un projet
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="inline-flex items-center gap-3 border border-ink/20 px-8 py-4 hover:border-violet hover:text-violet transition-colors duration-300"
              >
                Découvrir nos services
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-black/10 py-6 bg-secondary/40" data-testid="marquee">
        <Marquee speed={40} gradient={false}>
          {["Audit & stratégie", "Design & branding", "Développement web", "Photos & vidéos", "Réseaux sociaux", "Communication 360°"].map(
            (w, i) => (
              <span key={i} className="mx-8 inline-flex items-center gap-8 font-display text-3xl lg:text-5xl font-extrabold tracking-tight">
                {w} <span className="text-violet text-2xl">✦</span>
              </span>
            )
          )}
        </Marquee>
      </section>

      {/* BANNER */}
      <section
        ref={bannerRef}
        className="relative h-[72vh] min-h-[460px] overflow-hidden bg-ink"
        data-testid="home-banner"
      >
        <motion.img
          style={{ y: bannerY }}
          src="https://customer-assets-cm19k8pv.emergentagent.net/job_equinoxe-lot/artifacts/ed5d853afdfc07be_Duo%20chic%20sous%20l%E2%80%99aura%20violette.png"
          alt="Equinoxe Studio — une équipe créative"
          className="absolute inset-0 h-[120%] w-full object-cover object-center -top-[10%]"
          draggable="false"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full max-w-[1400px] px-6 lg:px-12 pb-14 lg:pb-20">
            <FadeUp>
              <p className="text-sm tracking-[0.25em] uppercase text-white/60 mb-4">Equinoxe Studio</p>
              <h2 className="font-display font-extrabold tracking-tighter text-white text-4xl sm:text-5xl lg:text-7xl leading-[0.9] max-w-4xl">
                Une équipe,<br />
                <span className="font-editorial italic font-normal text-violet-light">toute votre communication.</span>
              </h2>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-12 py-24 lg:py-40">
        <FadeUp>
          <p className="text-sm tracking-[0.25em] uppercase text-muted-foreground mb-4">Notre approche</p>
          <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[0.95]">
            Un studio, cinq expertises, <span className="font-editorial italic font-normal text-violet">une exigence.</span>
          </h2>
        </FadeUp>

        <div className="mt-20 grid md:grid-cols-3 gap-x-10 gap-y-16">
          {MANIFESTO.map((m, i) => (
            <FadeUp key={m.n} delay={i * 0.1}>
              <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-violet/30 text-violet">
                <m.icon size={30} strokeWidth={1.75} />
              </div>
              <h3 className="mt-6 font-display font-bold text-2xl tracking-tight">{m.title}</h3>
              <p className="mt-4 text-muted-foreground leading-relaxed">{m.body}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="border-t border-black/10" data-testid="home-services">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          {SERVICES.map((s, i) => (
            <Link
              to="/services"
              key={s.slug}
              className="group flex items-center justify-between gap-6 py-8 lg:py-10 border-b border-black/10 transition-colors hover:bg-ink hover:text-white -mx-6 lg:-mx-12 px-6 lg:px-12"
              data-testid={`home-service-${s.slug}`}
            >
              <div className="flex items-baseline gap-6 lg:gap-12">
                <span className="font-editorial text-lg text-violet">{s.index}</span>
                <span className="font-display font-extrabold tracking-tighter text-2xl sm:text-4xl lg:text-6xl leading-none">
                  {s.title}
                </span>
              </div>
              <ArrowUpRight className="shrink-0 opacity-40 group-hover:opacity-100 group-hover:text-violet-light transition-all group-hover:rotate-45" size={40} />
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-white" data-testid="home-cta">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 pt-24 lg:pt-40 pb-12 lg:pb-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          <FadeUp>
            <h2 className="font-display font-extrabold tracking-tighter text-5xl sm:text-6xl lg:text-8xl leading-[0.9]">
              Parlons de <br />
              <span className="font-editorial italic font-normal text-violet-light">votre projet.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15} className="flex flex-col items-start gap-4 lg:items-end shrink-0">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 bg-white text-ink px-8 py-4 hover:bg-violet hover:text-white transition-colors duration-300"
              data-testid="cta-contact-button"
            >
              Nous contacter <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <span className="text-white/50">Réponse sous 24h</span>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
